import { forwardRef, useEffect, useState } from 'react';
import cx from 'classnames';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { GroupedListDesign, ListDesign, ListHeader, ListItem, ListSeparator, VirtualListDesign } from '../ListBox';
import { Popover } from '../Popover';
import cn from './OptionPopper.css.json';
import './OptionPopper.css';

/**
 * A popper specifically for Inputs, Dropdowns, and Breadcrumbs. Please don't try to reuse
 * this for anything else unless it follows the same behavior (and is supposed to).
 */
import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
export const OptionPopper = /*#__PURE__*/forwardRef(function OptionPopper(props, ref) {
  const [minWidth, setMinWidth] = useState(undefined);
  const elementWidth = props.referenceElementRef.current?.getBoundingClientRect().width;
  useEffect(() => {
    if (props.referenceElementRef.current) {
      setMinWidth(elementWidth);
    }
  }, [props.referenceElementRef, elementWidth]);
  let separatorIndex = -1;
  let itemIndex = -1;
  const getOption = item => {
    if (item && !props.displayListItemIcon) {
      // remove icon if not displaying icons
      const {
        icon,
        ...listItem
      } = item;
      return listItem;
    }
    return item;
  };
  const getListItem = (item, index) => {
    const i = props.groups && props.nullItem ? index + 1 : index;
    return /*#__PURE__*/_createElement(ListItem, {
      ...getOption(item),
      onActionPress: props.onActionPress,
      onSelected: props.onSelected,
      index: i,
      key: i,
      tooltip: props.displayListItemIcon ? item?.iconTooltip : item?.tooltip,
      selected: props.focusedIndex === i,
      showSelectedPseudoFocus: props.showSelectedPseudoFocus,
      focused: !!props.listItemFocusable && props.focusedIndex === i,
      focusable: !!props.listItemFocusable && props.focusedIndex === i,
      ref: props.focusedIndex === i ? props.focusedItemRef : undefined,
      testId: item?.testId ?? postfixTestId(props.testId, `list-listItem-${i}`),
      tokens: props.listItemTokens,
      disabled: props.isItemDisabled?.(i)
    });
  };
  const getNullItem = item => {
    const index = 0;
    return /*#__PURE__*/_createElement(ListItem, {
      ...getOption(item),
      onActionPress: props.onActionPress,
      onSelected: props.onSelected,
      index: index,
      key: index,
      selected: props.focusedIndex === index,
      showSelectedPseudoFocus: props.showSelectedPseudoFocus,
      focused: !!props.listItemFocusable && props.focusedIndex === index,
      focusable: !!props.listItemFocusable && props.focusedIndex === index,
      ref: props.focusedIndex === index ? props.focusedItemRef : undefined,
      testId: item?.testId ?? postfixTestId(props.testId, `list-listItem-${index}`),
      tokens: props.listItemTokens,
      disabled: props.isItemDisabled?.(index)
    });
  };
  const listProps = {
    selectedIndex: props.selectedIndex,
    setFocusedIndex: props.setFocusedIndex,
    onSelectFocusedItem: props.onSelectFocusedItem,
    testId: postfixTestId(props.testId, 'list'),
    ...props.listTokens,
    id: props.listId,
    ariaLabelledBy: props.ariaLabelledBy,
    ariaLabel: props.ariaLabel
  };
  const popoverAnimation = {
    closed: {
      opacity: 0
    },
    open: {
      opacity: 1,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0.5, 0, 0.8, 1]
      }
    },
    exit: {
      opacity: 0,
      transition: {
        type: 'tween',
        duration: 0.25,
        ease: [0, 0.5, 0.2, 1]
      }
    }
  };
  const getGroups = () => {
    let index = -1;
    return !props.groups ? [] : props.groups.map((group, groupIndex) => ({
      ...group,
      header: group.header ? /*#__PURE__*/_jsx(ListHeader, {
        ...group.header,
        groupIndex: groupIndex,
        onHeaderSelected: props.onHeaderSelected
      }) : undefined,
      items: group.items?.map(item => {
        index++;
        return getListItem(item, index);
      })
    }));
  };
  return /*#__PURE__*/_jsx(Popover, {
    referenceElementRef: props.referenceElementRef,
    isOpen: props.isOpen,
    placement: "bottom-start",
    hideArrow: true,
    style: {
      ...props.style,
      maxHeight: props.style?.maxHeight ?? '75vh',
      minWidth: props.style?.minWidth ?? minWidth
    },
    className: cx(cn.optionPopper, props.className),
    testId: postfixTestId(props.testId, 'optionPopper'),
    ref: popoverRef => {
      if (popoverRef?.container) {
        setRef(ref, popoverRef?.container);
      }
    },
    onClose: props.onClose,
    onKeyDown: props.onKeyDown,
    returnFocus: props.returnFocus,
    focusLockProps: props.focusLockProps,
    ...props.popoverTokens,
    enableBrowserContextMenu: props.enableBrowserContextMenu,
    animation: {
      variants: popoverAnimation,
      ...props.animation
    },
    hideOnRefHidden: props.hideOnRefHidden,
    children: props.groups ? /*#__PURE__*/_jsx(GroupedListDesign, {
      className: cn.optionPopperList,
      groups: getGroups(),
      nullItem: props.nullItem ? getNullItem(props.nullItem) : undefined,
      ...listProps,
      ref: props.groupListRef
    }) : props.virtualItems ? /*#__PURE__*/_jsx(VirtualListDesign, {
      count: props.virtualItems.count,
      renderItem: ({
        index
      }) => getListItem(props.virtualItems?.renderItem({
        index
      }) ?? null, index),
      virtualizerRef: props.virtualItems.virtualizerRef,
      pageSizeRef: props.virtualItems.pageSizeRef,
      itemHeight: props.virtualItems.itemHeight,
      style: {
        width: `${props.virtualItems?.width ?? 150}px`,
        height: `100%`,
        // needs this explicitly set so list's scrolling container
        // has the same max-height
        maxHeight: props.style?.maxHeight ?? '75vh'
      },
      ...listProps,
      ref: props.ulRef
    }) : /*#__PURE__*/_jsx(ListDesign, {
      className: cn.optionPopperList,
      ...listProps,
      ref: props.ulRef,
      children: !props.items ? null : props.items.map(item => {
        if (item.separator) {
          separatorIndex++;
          return /*#__PURE__*/_jsx(ListSeparator, {}, `${separatorIndex}-separator`);
        } else {
          itemIndex++;
          return getListItem(item, itemIndex);
        }
      })
    })
  });
});
//# sourceMappingURL=OptionPopper.js.map